import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, TextField, Card, CardContent } from '@mui/material';
import FeedbackLogo from '../../../public/FeedbackLogo.svg';
import IdeaIcon from '../../../public/enhancement.svg';
import RepairIcon from '../../../public/issue.svg';
import IdeaGif from '../../../public/enhancement.gif';
import RepairGif from '../../../public/issue.gif';
import BallotBoxGif from '../../../public/ballot-box.gif';
import SuccessModal from '../SuccessModal';
import FilePicker from '../FilePicker';
import { FeedbackAPI } from '../../apis/FeedbackAPI';
import Cookies from 'js-cookie';
import { useTheme } from '@mui/material/styles';
import '../../types/theme'; // Import theme type extensions
import './FeedbackModal.css';
import { constants } from '../../constants';
import { ConfigContext } from '../ConfigContext';
import { useCurrentApplication } from '../ApplicationContext';
import { getDefaultApplication, getEmailFromSession, getUserFromCookie, isGenericGreenColor } from '@/utils/utilities';
import Sound from '../Sound/Sound';
import { checkProfanity } from '@/utils/profanityCheckUtils';
import { handleApiErrorModal } from '../ApiErrorModal';
import { NotificationAPI } from '../../apis/NotificationAPI';
import { usePlatformHost } from '../../hooks/usePlatformHost';
const FeedbackTypeCard = ({ type, title, description, icon, gif, isSelected, onClick }) => {
    const theme = useTheme();
    const { configState } = React.useContext(ConfigContext);
    React.useEffect(() => {
        const cssVars = {
            '--gradient-background': isGenericGreenColor(configState?.settings?.button_color)
                ? 'linear-gradient(180deg, #1BA4DE 0%, #3EBC32 100%)'
                : configState?.settings?.button_color,
            '--text-color': configState?.settings?.workspace_name_color ||
                theme.palette.text.primary,
            '--button-bg-color': configState?.settings?.button_color ||
                theme.palette.primary.dark,
            '--button-text-color': configState?.settings?.button_text_color ||
                theme.palette.primary.contrastText
        };
        for (const [key, value] of Object.entries(cssVars)) {
            document.documentElement.style.setProperty(key, value);
        }
    }, [configState]);
    return (_jsx(Card, { className: `feedback-type-card ${isSelected ? 'selected' : ''}`, onClick: onClick, "data-testid": `feedback-type-${type}`, children: _jsxs(CardContent, { className: "feedback-card-content", children: [_jsx(Box, { className: "feedback-card-header", children: _jsx(Box, { p: '4px', className: `feedback-card-icon ${isSelected ? 'selected' : ''}`, children: isSelected ? (_jsx("img", { src: gif, alt: type, width: 24, height: 24 })) : (_jsx("img", { src: icon, alt: type })) }) }), _jsxs(Box, { children: [_jsx(Typography, { variant: "body1", fontWeight: 600, className: "feedback-card-title", children: title }), _jsx(Typography, { variant: "body2", className: "feedback-card-description", children: description })] })] }) }));
};
const FeedbackModal = ({ open, onClose, width = '800px', height = 'auto' }) => {
    const [selectedType, setSelectedType] = useState('enhancement');
    const [description, setDescription] = useState('');
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [ticketNumber, setTicketNumber] = useState('');
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidFiles, setIsValidFiles] = useState(true);
    const [play, setPlay] = useState(false);
    const [type, setType] = useState('success');
    const [hasProfanity, setHasProfanity] = useState(false);
    const getPlatformHost = usePlatformHost();
    const [platformHost, setPlatformHost] = useState('');
    const [isPlatformHostLoading, setIsPlatformHostLoading] = useState(true);
    useEffect(() => {
        getPlatformHost()
            .then(setPlatformHost)
            .finally(() => setIsPlatformHostLoading(false));
    }, [getPlatformHost]);
    const productId = Cookies.get('Product Selected');
    const currentApp = useCurrentApplication(productId ? Number(productId) : 0);
    const appDetails = sessionStorage.getItem('current_application')
        ? JSON.parse(sessionStorage.getItem('current_application') || '{}')
        : currentApp;
    const MAX_CHARS = 1000;
    const remainingChars = MAX_CHARS - description.length;
    React.useEffect(() => {
        if (!open) {
            setIsSubmitting(false);
            setIsValidFiles(true);
            setHasProfanity(false);
        }
    }, [open]);
    const processFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result;
                const base64 = result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };
    const processAttachments = async (files) => {
        const attachments = await Promise.all(files.map(async (file) => {
            const base64Content = await processFileToBase64(file);
            return {
                contentType: file.type,
                content: base64Content
            };
        }));
        return attachments;
    };
    const handleSubmit = async () => {
        if (!description.trim() ||
            description.length > MAX_CHARS ||
            isSubmitting ||
            hasProfanity) {
            return;
        }
        setIsSubmitting(true);
        const defaultApp = getDefaultApplication();
        const user = getUserFromCookie();
        const applicationName = appDetails?.name || defaultApp.appName;
        const ownerEmails = Array.isArray(appDetails?.details?.applicationOwner)
            ? appDetails.details.applicationOwner.filter((email) => typeof email === 'string' && email.trim().length > 0)
            : undefined;
        try {
            const attachments = await processAttachments(attachedFiles);
            let feedbackRequest = {
                resourceType: 'feedback',
                type: selectedType,
                title: `[${selectedType.toUpperCase()}] ${appDetails?.id || defaultApp.appName}`,
                source: {
                    source_url: `${globalThis.window.location.href}`,
                    application_name: applicationName,
                    application_id: String(appDetails?.id || defaultApp.appId)
                },
                description: description,
                attachment: attachments,
                submittedBy: {
                    name: user.name,
                    email: user.email
                },
                meta: {}
            };
            const data = await FeedbackAPI.submitFeedback(feedbackRequest);
            if (data) {
                const githubIssueId = data?.id;
                setTicketNumber(githubIssueId);
                if (platformHost) {
                    NotificationAPI.sendFeedbackEmailNotification({
                        type: selectedType,
                        description,
                        applicationName,
                        submittedBy: {
                            name: user.name,
                            email: user.email
                        },
                        referenceNumber: githubIssueId,
                        ownerEmails
                    }, platformHost).catch((err) => {
                        console.error('Failed to send feedback email notification:', err);
                    });
                }
                setSuccessModalOpen(true);
                setType('success');
                setPlay(true);
                onClose();
            }
        }
        catch (error) {
            // Log comprehensive backend error information
            console.error('Error submitting feedback:', {
                message: error.message,
                backendResponse: error.backendResponse,
                statusCode: error.statusCode,
                fullError: error
            });
            const isProfanity = error.backendResponse?.errors?.description?.includes('profanity');
            if (isProfanity)
                setHasProfanity(true);
            const refId = error.backendResponse?.errors?.errorLogRef ??
                error.backendResponse?.errorLogRef ??
                error.backendResponse?.log_ref ??
                undefined;
            setType('error');
            setPlay(true);
            handleApiErrorModal(true, {
                title: constants.feedback_error_modal_title,
                description: isProfanity
                    ? constants.feedback_error_modal_profanity_description
                    : constants.feedback_error_modal_description(getEmailFromSession()),
                refId
            });
            onClose();
        }
        finally {
            setIsSubmitting(false);
            setDescription('');
            setAttachedFiles([]);
            setSelectedType('enhancement');
        }
    };
    const handleSuccessModalClose = () => {
        setSuccessModalOpen(false);
        setSelectedType('enhancement');
        setDescription('');
        setTicketNumber('');
        setAttachedFiles([]);
        setIsSubmitting(false);
        setHasProfanity(false);
    };
    const handleDescriptionChange = (event) => {
        const value = event.target.value;
        setDescription(value);
        setHasProfanity(checkProfanity(value));
    };
    const handleCancelFeedback = () => {
        onClose();
        setSelectedType('enhancement');
        setDescription('');
        setAttachedFiles([]);
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Dialog, { open: open, onClose: onClose, keepMounted: attachedFiles.length > 0, maxWidth: "md", fullWidth: true, "aria-labelledby": "feedback-modal-title", "data-testid": "feedback-modal", PaperProps: {
                    sx: {
                        borderRadius: '24px',
                        width: width,
                        height: height
                    }
                }, children: [_jsxs(DialogTitle, { id: "feedback-modal-title", className: "feedback-modal-title", children: [_jsx("img", { src: FeedbackLogo, alt: "Feedback" }), _jsx(Typography, { variant: "h6", component: "div", children: constants.feedback })] }), _jsxs(DialogContent, { className: "feedback-modal-content", children: [_jsxs(Box, { className: "feedback-cards-container", children: [_jsx(FeedbackTypeCard, { type: "enhancement", title: constants.enhancement_title, description: constants.enhancement_desc, icon: IdeaIcon, gif: IdeaGif, isSelected: selectedType === 'enhancement', onClick: () => setSelectedType('enhancement') }), _jsx(FeedbackTypeCard, { type: "issue", title: constants.issue_title, description: constants.issue_desc, icon: RepairIcon, gif: RepairGif, isSelected: selectedType === 'issue', onClick: () => setSelectedType('issue') })] }), _jsxs(Box, { children: [_jsx(TextField, { multiline: true, rows: 4, label: selectedType === 'issue'
                                            ? constants.issue_description_placeholder
                                            : constants.suggestion_description_placeholder, value: description, onChange: (e) => handleDescriptionChange(e), required: true, "data-testid": "feedback-description", variant: "outlined", error: remainingChars < 0, className: `feedback-textarea ${remainingChars < 0 ? 'error' : ''}` }), _jsxs(Box, { className: "feedback-char-count", children: [hasProfanity ? (_jsx(Typography, { variant: "caption", color: "error", children: constants.feedback_profanity_error })) : (_jsx(Typography, { variant: "caption", color: remainingChars < 0
                                                    ? 'error'
                                                    : 'text.secondary', children: remainingChars >= 0 ? (constants.max_1000_chars) : (_jsxs(_Fragment, { children: ["\uD83D\uDD34 ", constants.reached_max_limit] })) })), _jsxs(Typography, { variant: "caption", color: remainingChars < 0
                                                    ? 'error'
                                                    : 'text.secondary', children: [description.length, "/1000"] })] })] }), _jsxs(Box, { className: "feedback-attachment-section", children: [_jsx(Typography, { variant: "subtitle2", className: "feedback-attachment-title", children: constants.attachment_label }), _jsx(FilePicker, { onFilesChange: (files, validity) => {
                                            setAttachedFiles(files);
                                            setIsValidFiles(validity);
                                        }, maxTotalSize: 10 * 1024 * 1024, acceptedTypes: ['.png', '.jpg', '.txt'], multiple: true })] })] }), _jsxs(DialogActions, { className: "feedback-modal-actions", children: [_jsx(Button, { onClick: handleCancelFeedback, variant: "text", "data-testid": "feedback-cancel-button", className: "feedback-cancel-button", children: constants.cancel }), description.trim() && isValidFiles && (_jsx(Button, { onClick: handleSubmit, variant: "contained", color: "success", disabled: isSubmitting ||
                                    description.length > MAX_CHARS ||
                                    hasProfanity ||
                                    isPlatformHostLoading, "data-testid": "feedback-submit-button", className: "feedback-submit-button", children: isSubmitting
                                    ? constants.submitting
                                    : constants.submit }))] })] }), _jsx(SuccessModal, { open: successModalOpen, onClose: handleSuccessModalClose, title: constants.success_title, message: _jsxs("span", { children: [constants.ticket_number + ' ', _jsx("strong", { children: ticketNumber }), ' ' + constants.for_reference] }), buttonText: constants.close, gif: BallotBoxGif }), _jsx(Sound, { type: type, play: play, setPlay: setPlay })] }));
};
export default FeedbackModal;
